import React, { FC, useCallback, useEffect, useState } from "react";
import {
  Grid,
  Snackbar,
  Box,
  Typography,
  Button,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import Alert from "@mui/material/Alert";
import { useDispatch, useSelector } from "react-redux";
import { IRootReducer, UserModel } from "../../../reducers";
import InviteUser from "../user-operation/invite-user";
import ChangeUserStatus from "../user-operation/change-user-status";
import ReInviteUser from "../user-operation/re-invite-user";
import { AdminStoreSuccess } from "../../../enums/store-messages/admin";
import { getUsersRequest, getAgencyByUserRequest, getGroupsForUserRequest } from "../../../actions";
import {
  DataGrid,
  GridLoadingOverlay,
  GridRowSelectionModel,
  GridToolbarContainer,
  GridToolbarDensitySelector,
  GridToolbarFilterButton,
} from "@mui/x-data-grid";
import { ParsePhoneNumberUtil } from "../../../utils/string-utils";
import EditUser from "../user-operation/edit-user";

import { useAuthContext } from "../../../core/auth-context";
import { JogMap } from "../../../reducers/states/agency/jogs";

export interface UserGridRow {
  id: string;
  name: string;
  jog: string;
  agency: string;
  enabled: string;
  status: string;
  email?: string;
  phoneNumber: string;
  cognitoUsername: string;
}

const AdminGrid: FC = () => {
  const [rowSelection, setRowSelection] = useState({} as UserGridRow);
  const [userSelection, setUserSelection] = useState({} as UserModel);
  const [selectedRows, setSelectedRows] = useState([] as GridRowSelectionModel);
  const [invitedEmail, setInvitedEmail] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [gridData, setGridData] = useState([] as UserGridRow[]);
  const [isLoading, setIsLoading] = useState(false);
  const [paginationModel, setPaginationModel] = useState({
    pageSize: 25,
    page: 0,
  });
  const dispatch = useDispatch();

  const store = useSelector((state: IRootReducer) => state.adminReducer);

  const { authState } = useAuthContext();

  const showLoadingAnimation = useCallback(() => {
    const timeoutId = setTimeout(() => {
      setIsLoading(false);
    }, 1250);

    return function cleanup() {
      clearTimeout(timeoutId);
    };
  }, []);

  useEffect(() => {
    dispatch(getUsersRequest((authState.email ?? "").toLowerCase()));
    dispatch(getAgencyByUserRequest(authState.email?.toLowerCase() ?? ""));
    dispatch(getGroupsForUserRequest(authState.email?.toLowerCase() ?? ""));

    setIsLoading(true);
    showLoadingAnimation();
  }, [dispatch, showLoadingAnimation]);

  useEffect(() => {
    const apiRows: UserGridRow[] = store.users?.map((u) => {
      return {
        id: u.email,
        name: u.firstName + u.lastName ? u.firstName + " " + u.lastName : "N/A",
        jog: (u.jogId && JogMap.get(u.jogId)?.shortName) || "",
        agency: u.agency ?? "N/A",
        enabled: u.enabled === "Y" ? "Active" : "Disabled",
        // If a user has a congitoUsername, then they have completed signup
        status: u.cognitoUsername ? "Confirmed" : "Unconfirmed",
        email: u.email,
        phoneNumber: u.phoneNumber ? ParsePhoneNumberUtil(u.phoneNumber) : "N/A",
        // If a user is an original Cognito User preSAW, use their email as their cognitoUsername
        cognitoUsername: u.cognitoUsername ?? u.email,
      };
    });

    setGridData(apiRows);
  }, [store.users]);

  useEffect(() => {
    if (store.successMessage) {
      switch (store.successMessage) {
        case AdminStoreSuccess.CREATE_AND_INVITE_USER_SUCCESS:
          dispatch(getUsersRequest((authState.email ?? "").toLowerCase()));
          break;
        case AdminStoreSuccess.REINVITE_USER_SUCCESS:
        case AdminStoreSuccess.DISABLE_USER_SUCCESS:
        case AdminStoreSuccess.ENABLE_USER_SUCCESS:
        case AdminStoreSuccess.UPDATE_USER_SUCCESS:
          dispatch(getUsersRequest((authState.email ?? "").toLowerCase()));
          setRowSelection({} as UserGridRow);
          setSelectedRows([] as GridRowSelectionModel);
          break;
      }

      setSuccessMessage(store.successMessage);
    }
    if (store.failureMessage) {
      setErrorMessage(store.failureMessage);
    }
  }, [store.successMessage, store.failureMessage, dispatch, invitedEmail]);

  function showToolbar() {
    return (
      <GridToolbarContainer>
        <GridToolbarFilterButton />
        <GridToolbarDensitySelector />
      </GridToolbarContainer>
    );
  }

  const handleRowSelection = (newSelectedRows: GridRowSelectionModel) => {
    setSelectedRows(newSelectedRows.slice(-1));
    if (newSelectedRows.length === 1) {
      setRowSelection(gridData.find((d) => d.id === newSelectedRows[0])!);
      setUserSelection(store.users.find((d) => d.email === newSelectedRows[0])!);
    }
  };

  const columns = [
    { field: "name", headerName: "Name", flex: 1 },
    { field: "jog", headerName: "Primary JOG", flex: 0.75 },
    { field: "agency", headerName: "Agency", flex: 0.5 },
    { field: "email", headerName: "Email", flex: 1.25 },
    { field: "phoneNumber", headerName: "Phone Number", flex: 0.75 },
    { field: "enabled", headerName: "VCC Status", flex: 0.5 },
    { field: "status", headerName: "Account Status", flex: 0.5 },
  ];

  const getGridHeight = () => {  
    const headerHeight = 64;
    const appMainBoxMargins = 16;
    const title = 53;
    const total = headerHeight + appMainBoxMargins + title;
    return `calc(100vh - ${total}px`;
  }

  return (
    <>
      <Grid container alignItems="center" justifyContent="space-between">
        <Grid item>
          <Typography variant="h6" component="h1">User Admin</Typography>
        </Grid>
        <Grid item container xs id="admin-grid" alignItems="center" justifyContent="flex-end">
          <Grid item m={1}>
            <EditUser
              props={{
                rowCountSelected: selectedRows?.length,
                userSelection: userSelection,
              }}
            />
          </Grid>
          <Grid item m={1}>
            <ChangeUserStatus
              props={{
                rowCountSelected: selectedRows?.length,
                userSelection: userSelection,
              }}
            />
          </Grid>
          <Grid item m={1}>
            <ReInviteUser
              props={{
                rowCountSelected: selectedRows?.length,
                rowSelection: rowSelection,
              }}
            />
          </Grid>
          <Grid item m={1}>
            <InviteUser
              props={{
                setInvitedEmail: (invitedEmail: string) => setInvitedEmail(invitedEmail),
              }}
            />
          </Grid>
          <Grid item m={1}>
            <Button
              aria-label="Reload Users"
              color="inherit"
              endIcon={<RefreshIcon />}
              onClick={() => dispatch(getUsersRequest((authState.email ?? "").toLowerCase()))}
              style={{ minWidth: "150px" }}
              variant="contained"
            >
              Refresh
            </Button>
          </Grid>
        </Grid>
      </Grid>
        <Box style={{ height: getGridHeight() }}>
          <DataGrid
            checkboxSelection
            columns={columns}
            disableColumnSelector
            onPaginationModelChange={setPaginationModel}
            onRowSelectionModelChange={(s) => handleRowSelection(s)}
            paginationModel={paginationModel}
            rows={gridData}
            rowSelectionModel={selectedRows}
            slots={{
              loadingOverlay: GridLoadingOverlay,
              toolbar: showToolbar,
            }}
            loading={isLoading}
          />
        </Box>
      
      <Snackbar
        open={errorMessage !== ""}
        autoHideDuration={5000}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        onClose={() => setErrorMessage("")}
      >
        <Alert onClose={() => setErrorMessage("")} severity="error">
          {errorMessage}
        </Alert>
      </Snackbar>
      <Snackbar
        open={successMessage !== ""}
        autoHideDuration={5000}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        onClose={() => setSuccessMessage("")}
      >
        <Alert onClose={() => setSuccessMessage("")} severity="success">
          {successMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default AdminGrid;
